import React from 'react';
import { X } from 'lucide-react';
import './TermsConditions.css';

interface TermsConditionsProps {
  onClose: () => void;
}

const TermsConditions: React.FC<TermsConditionsProps> = ({ onClose }) => {
  return (
    <div className="terms-overlay" onClick={onClose}>
      <div className="terms-modal" onClick={e => e.stopPropagation()}>
        <button className="terms-close-btn" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="terms-container">

          {/* ── SECTION 1: FOOD SAFETY POLICY ── */}
          <h1 className="terms-title">HOMECOOKT – Food Safety Policy</h1>
          <p className="effective-date">Effective Date: June 1, 2026</p>

          <section className="terms-section">
            <h2 className="section-title">1. Purpose and Scope</h2>
            <div className="section-content">
              <p>This Food Safety Policy governs all food prepared, listed, sold, or delivered through the HomeCookt platform ("Platform") in the United Kingdom. It applies to all users including home cooks ("Cooks"), customers ("Users"), and any third-party facilitators.</p>
              <p>HomeCookt is a technology platform connecting independent home cooks with customers and does not itself prepare, handle, store, or deliver food.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">2. Regulatory Compliance</h2>
            <div className="section-content">
              <p>All home cooks must comply with:</p>
              <ul>
                <li>The Food Safety Act 1990 and the Food Standards Act 1999</li>
                <li>Food Standards Agency (FSA) guidance and codes of practice</li>
                <li>Local authority food business registration requirements</li>
                <li>Any other applicable UK food hygiene and safety legislation</li>
              </ul>
              <p>Cooks are solely responsible for determining whether their activity requires registration, licence, or approval from their local authority.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">3. Independent Food Business Status</h2>
            <div className="section-content">
              <p>Each home cook operates as an independent food business operator (FBO) under UK food law.</p>
              <p>HomeCookt:</p>
              <ul>
                <li>Does not act as a food business operator</li>
                <li>Does not certify kitchens or food safety compliance</li>
                <li>Does not guarantee quality, safety, or suitability of any food</li>
              </ul>
              <h3 className="section-subtitle">3.1 Marketplace Disclaimer</h3>
              <p>HomeCookt simply acts as a marketplace platform showcasing available food offerings. HomeCookt does not assume responsibility or liability for any impact, positive or negative, arising from food safety deficiencies, hygiene failures, preparation issues, contamination, or health-related consequences associated with food supplied by independent cooks.</p>
              <p>This includes, without limitation, foodborne illness, allergic reactions, contamination events, and any condition that may reasonably be considered abnormal from the highest standards of physical health, safety, wellbeing, or public health.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">4. Mandatory Hygiene Standards</h2>
            <div className="section-content">
              <p>All home cooks must ensure:</p>
              <ul>
                <li>Clean and sanitary food preparation environment</li>
                <li>Regular cleaning of utensils, surfaces, and storage areas</li>
                <li>Use of safe potable water</li>
                <li>Effective pest control measures</li>
                <li>Prevention of cross-contamination between raw and cooked foods</li>
              </ul>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">5. Food Handling Requirements</h2>
            <div className="section-content">
              <p>Cooks must:</p>
              <ul>
                <li>Follow safe cooking temperatures for all food types</li>
                <li>Use fresh and unexpired ingredients</li>
                <li>Store food at safe temperatures prior to delivery</li>
                <li>Ensure hygienic packaging prior to handover</li>
                <li>Avoid preparation of food in unsafe or contaminated conditions</li>
              </ul>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">6. Medical Fitness &amp; Personal Hygiene</h2>
            <div className="section-content">
              <p>Cooks must not prepare food if they:</p>
              <ul>
                <li>Are suffering from infectious diseases or symptoms (including fever, vomiting, diarrhoea, respiratory infection)</li>
                <li>Have been advised by a medical professional to avoid food handling</li>
              </ul>
              <p>Cooks must maintain personal hygiene, including clean clothing and safe handling practices.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">7. Allergen Disclosure (Strict Requirement)</h2>
            <div className="section-content">
              <p>Under UK law (retained EU Regulation 1169/2011), cooks must disclose the 14 major allergens:</p>
              <ul>
                <li>Celery, cereals containing gluten, crustaceans, eggs, fish, lupin, milk, molluscs, mustard, peanuts, sesame, soybeans, sulphur dioxide/sulphites, tree nuts</li>
              </ul>
              <p>Where cross-contamination risk exists, it must be clearly disclosed.</p>
              <p>Failure to disclose allergens may result in suspension or termination.</p>
              <p>Cooks assume full responsibility and liability for any injury, illness, allergic reaction, health consequence, loss, damage, claim, or legal action resulting from non-disclosure, inaccurate disclosure, improper preparation, or contamination.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">8. Packaging &amp; Transport Safety</h2>
            <div className="section-content">
              <ul>
                <li>Food must be packaged in food-grade, hygienic packaging</li>
                <li>Packaging must prevent contamination during transport</li>
                <li>Hot and cold foods must be separated where applicable</li>
                <li>Sealing of food is strongly recommended</li>
              </ul>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">9. Enforcement &amp; Limitation of Liability</h2>
            <div className="section-content">
              <p>HomeCookt reserves the right to remove listings, suspend or terminate accounts, and report serious violations to the relevant local authority or the Food Standards Agency.</p>
              <p>To the maximum extent permitted by English law, HomeCookt is not liable for foodborne illness, allergic reactions, or other harm arising from food supplied through the Platform. Nothing in this Policy excludes liability for death or personal injury caused by negligence.</p>
            </div>
          </section>

          {/* ── DIVIDER ── */}
          <div className="policy-divider" />

          {/* ── SECTION 2: TERMS & CONDITIONS ── */}
          <h1 className="terms-title">HOMECOOKT – Terms &amp; Conditions</h1>
          <p className="effective-date">Effective Date: June 1, 2026</p>

          <p className="terms-intro">
            These Terms &amp; Conditions ("Terms") govern access to and use of the HomeCookt platform, including website, mobile applications, APIs, and related services. By accessing or using HomeCookt, users agree to be bound by these Terms.
          </p>

          <section className="terms-section">
            <h2 className="section-title">1. Platform</h2>
            <div className="section-content">
              <p>HomeCookt is a technology marketplace connecting independent home cooks with customers in the United Kingdom. HomeCookt does not prepare, sell, store, or deliver food and is not a restaurant, food business, or catering service.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">2. Eligibility</h2>
            <div className="section-content">
              <p>Users must be aged 18 or over and legally capable of entering binding contracts under English law. Home cooks must additionally comply with all applicable UK food safety laws and local authority requirements.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">3. Accounts</h2>
            <div className="section-content">
              <p>Users are responsible for maintaining account confidentiality, ensuring the accuracy of provided information, and all activity conducted under their account.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">4. Home Cooks</h2>
            <div className="section-content">
              <p>Home cooks are independent contractors solely responsible for food preparation, hygiene and safety compliance, allergen disclosure, compliance with UK food safety laws and local authority food business registration, and order fulfilment. No employment, partnership, or agency relationship exists between HomeCookt and cooks.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">5. Customers</h2>
            <div className="section-content">
              <p>Customers must review ingredients and allergen information before ordering, ensure suitability of food for their dietary needs, and use personal judgement when consuming home-prepared food.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">6. Orders</h2>
            <div className="section-content">
              <p>Orders constitute a direct contract between customer and cook. HomeCookt is not a party to that contract. Acceptance of orders is subject to cook availability.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">7. Payments</h2>
            <div className="section-content">
              <p>HomeCookt may process payments on behalf of users and charge service fees or platform fees. All pricing and fees will be disclosed prior to purchase.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">8. Refunds &amp; Cancellations</h2>
            <div className="section-content">
              <p>Refund and cancellation rights are subject to the Consumer Rights Act 2015 where applicable. HomeCookt may intervene in disputes but does not guarantee refunds beyond statutory requirements.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">9. Prohibited Conduct</h2>
            <div className="section-content">
              <p>Users must not misrepresent food, ingredients, or identity; list unsafe or contaminated food; violate food safety laws; engage in fraud or abusive behaviour; or circumvent platform payments.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">10. Privacy &amp; Data Protection</h2>
            <div className="section-content">
              <p>User data is processed in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. For full details, see our Privacy Policy.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">11. Suspension &amp; Termination</h2>
            <div className="section-content">
              <p>HomeCookt may suspend or terminate accounts for safety violations, fraudulent activity, legal non-compliance, or harmful conduct toward users or platform integrity.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">12. Limitation of Liability</h2>
            <div className="section-content">
              <p>To the maximum extent permitted by English law, HomeCookt is not liable for indirect, incidental, or consequential losses, or for food-related harm caused by independent cooks. Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, or fraudulent misrepresentation.</p>
            </div>
          </section>

          <section className="terms-section">
            <h2 className="section-title">13. Governing Law</h2>
            <div className="section-content">
              <p>These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
